import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useAssignmentAlert } from './hooks/useAssignmentAlert';
import AgentStatusMenu from './components/AgentStatusMenu';
import NotificationBell from './components/NotificationBell';
import Dashboard from './pages/Dashboard';
import Inbox from './pages/Inbox';
import Contacts from './pages/Contacts';
import Broadcasts from './pages/Broadcasts';
import NewBroadcast from './pages/NewBroadcast';
import Analytics from './pages/Analytics';
import Catalog from './pages/Catalog';
import Quotations from './pages/Quotations';
import WFM from './pages/WFM';
import Links from './pages/Links';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import WebhookDebug from './pages/WebhookDebug';
import FlowTriggers from './pages/FlowTriggers';
import Automations from './pages/Automations';
import AutomationEditor from './pages/AutomationEditor';
import Templates from './pages/Templates';
import FAQ from './pages/FAQ';
import ProductFeature from './pages/ProductFeature';
import AdminUsers from './pages/AdminUsers';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminMonitoring from './pages/AdminMonitoring';
import AdminAudit from './pages/AdminAudit';
import { initErrorReporter } from './utils/errorReporter';
import api from './api';
import BroadcastDetail from './pages/BroadcastDetail';
import './App.css';

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  const toastStyle = theme === 'dark'
    ? { background: '#141a17', color: '#f2f7f4', border: '1px solid #2b3530' }
    : { background: '#ffffff', color: '#0f172a', border: '1px solid #e2e8f0' };

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ style: toastStyle }} />
        <AppRoutes theme={theme} toggleTheme={toggleTheme} />
      </BrowserRouter>
    </AuthProvider>
  );
}

function AppRoutes({ theme, toggleTheme }) {
  const { user, loading, impersonating, returnToAdmin } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Uncaught browser errors show up on the admin System Monitor page
  useEffect(() => { if (user) initErrorReporter(); }, [user]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  if (loading) return <div className="loading-screen"><span>Loading…</span></div>;

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/products/:slug" element={<ProductFeature />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <>
      {!online && <div className="offline-banner">⚡ Offline — changes will sync when you reconnect</div>}
      {impersonating && (
        <div style={{
          background: 'linear-gradient(90deg, #b45309, #d97706)', color: '#fff',
          padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          position: 'sticky', top: 0, zIndex: 1000,
        }}>
          🔑 Logged in as <strong>{user.name}</strong> ({user.email})
          <button
            onClick={returnToAdmin}
            style={{
              background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 6, padding: '3px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >← Return to my admin account</button>
        </div>
      )}
      <div className="app-layout">
        <button
          className="mobile-nav-toggle"
          onClick={() => setMobileNavOpen(open => !open)}
          aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileNavOpen}
        >
          <span className={`hamburger-icon${mobileNavOpen ? ' open' : ''}`} aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
        {mobileNavOpen && <button className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" />}
        <Sidebar theme={theme} toggleTheme={toggleTheme} mobileOpen={mobileNavOpen} onNavigate={() => setMobileNavOpen(false)} />
        <div className="content-column">
          <TokenTopNav />
          <main className="main-content">
            <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {ROUTES.filter(r => canAccess(user, r.page)).map(r => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/products/:slug" element={<ProductFeature />} />
            {isAdmin(user) && <Route path="/admin" element={<AdminUsers />} />}
            {isSuperAdmin(user) && <Route path="/admin/usage" element={<AdminAnalytics />} />}
            {isSuperAdmin(user) && <Route path="/admin/monitoring" element={<AdminMonitoring />} />}
            {isSuperAdmin(user) && <Route path="/admin/audit" element={<AdminAudit />} />}
            {/* Disallowed paths fall through to the dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </>
  );
}

function TokenTopNav() {
  const { user, switchWorkspace } = useAuth();
  const [usage, setUsage] = useState(null);
  const [holdsOpen, setHoldsOpen] = useState(false);
  const [holds, setHolds] = useState([]);
  const [holdsLoading, setHoldsLoading] = useState(false);

  const loadUsage = () => api.get('/billing/summary')
    .then(r => setUsage(r.data))
    .catch(() => {});

  const loadHolds = async () => {
    setHoldsLoading(true);
    try {
      const r = await api.get('/billing/holds');
      setHolds(r.data.holds || []);
    } catch { setHolds([]); }
    setHoldsLoading(false);
  };

  const openHolds = () => {
    setHoldsOpen(true);
    loadHolds();
  };

  const retryHold = async id => {
    await api.post(`/billing/holds/${id}/retry`).catch(() => {});
    await Promise.all([loadHolds(), loadUsage()]);
  };

  const cancelHold = async id => {
    await api.delete(`/billing/holds/${id}`).catch(() => {});
    await Promise.all([loadHolds(), loadUsage()]);
  };

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/billing/summary')
      .then(r => { if (alive) setUsage(r.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 15000);
    window.addEventListener('focus', load);
    window.addEventListener('token-balance-refresh', load);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('focus', load);
      window.removeEventListener('token-balance-refresh', load);
    };
  }, [user?._id, user?.active_workspace]);

  const fmt = value => new Intl.NumberFormat().format(value || 0);
  const selectedWorkspace = user?.workspaces?.find(workspace => workspace.id === user.active_workspace);
  return (
    <header className="workspace-topnav" aria-label="Account usage">
      {user?.workspaces?.length > 1 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Workspace</span>
          <select className="select" aria-label="Workspace" value={user.active_workspace || ''}
            onChange={e => switchWorkspace(e.target.value).catch(() => {})}>
            {user.workspaces.map(workspace => (
              <option key={workspace.id} value={workspace.id} disabled={!workspace.available}>
                {workspace.name}{workspace.linked ? ' · shared' : ' · mine'}{!workspace.available ? ' (unavailable)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="token-usage-label">WhatsApp usage</div>
      {selectedWorkspace?.linked && <div className="token-usage-label">
        {selectedWorkspace.share_tokens ? 'WABA owner pays' : 'Your account pays'}
      </div>}
      <div className={`token-usage-pill${usage && usage.balance <= 0 ? ' empty' : ''}`}>
        <span className="token-dot" aria-hidden="true" />
        <strong>{usage ? fmt(usage.balance) : '…'}</strong> tokens remaining
      </div>
      {usage && <div className="token-used-total">{fmt(usage.used_total)} used</div>}
      {usage && (usage.held_count > 0 || usage.failed_hold_count > 0) && (
        <button className="token-hold-pill" onClick={openHolds}>
          <span aria-hidden="true">⏸</span>
          <strong>{fmt(usage.held_count)}</strong> on hold
          {usage.failed_hold_count > 0 && <span className="token-hold-failed">{fmt(usage.failed_hold_count)} failed</span>}
        </button>
      )}
      {holdsOpen && (
        <div className="token-hold-overlay" onClick={() => setHoldsOpen(false)}>
          <section className="token-hold-panel" onClick={e => e.stopPropagation()} aria-label="Token on-hold queue">
            <div className="token-hold-head">
              <div>
                <h2>Token on-hold queue</h2>
                <p>Messages send oldest-first automatically when enough tokens are available.</p>
              </div>
              <button className="btn-close" onClick={() => setHoldsOpen(false)} aria-label="Close">×</button>
            </div>
            {holdsLoading ? (
              <div className="token-hold-empty">Loading held messages…</div>
            ) : holds.length === 0 ? (
              <div className="token-hold-empty">No messages are currently on hold.</div>
            ) : (
              <div className="token-hold-list">
                {holds.map(hold => (
                  <article className="token-hold-item" key={hold._id}>
                    <div className="token-hold-item-main">
                      <div className="token-hold-item-title">
                        <span className={`token-hold-status ${hold.status}`}>{hold.status.replace('_', ' ')}</span>
                        <strong>{hold.phone}</strong>
                        <span>{hold.required_tokens} token{hold.required_tokens === 1 ? '' : 's'}</span>
                      </div>
                      <div className="token-hold-preview">{hold.preview}</div>
                      <div className="token-hold-meta">
                        {hold.source.replaceAll('_', ' ')} · {hold.category} · {new Date(hold.createdAt).toLocaleString()}
                      </div>
                      {hold.last_error && <div className="token-hold-error">{hold.last_error}</div>}
                    </div>
                    <div className="token-hold-actions">
                      {['pending', 'failed'].includes(hold.status) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => retryHold(hold._id)}>Retry</button>
                      )}
                      {['pending', 'failed'].includes(hold.status) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => cancelHold(hold._id)}>Cancel</button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </header>
  );
}

// Route definitions gated by page-access key (see server config/access.js)
const ROUTES = [
  { path: '/dashboard',     element: <Dashboard />,    page: 'dashboard' },
  { path: '/inbox',         element: <Inbox />,        page: 'inbox' },
  { path: '/contacts',      element: <Contacts />,     page: 'contacts' },
  { path: '/broadcasts',    element: <Broadcasts />,   page: 'broadcasts' },
  { path: '/broadcasts/new', element: <NewBroadcast />, page: 'broadcasts' },
  { path: '/broadcasts/:id', element: <BroadcastDetail />, page: 'broadcasts' },
  { path: '/analytics',     element: <Analytics />,    page: 'analytics' },
  { path: '/catalog',       element: <Catalog />,      page: 'catalog' },
  { path: '/quotations',    element: <Quotations />,   page: 'quotations' },
  { path: '/wfm',           element: <WFM />,          page: 'wfm' },
  { path: '/links',         element: <Links />,        page: 'links' },
  { path: '/flow-triggers', element: <FlowTriggers />, page: 'flow-triggers' },
  { path: '/automations',   element: <Automations />,  page: 'automations' },
  { path: '/automations/:id', element: <AutomationEditor />, page: 'automations' },
  { path: '/templates',     element: <Templates />,    page: 'templates' },
  { path: '/settings',      element: <Settings />,     page: 'settings' },
  { path: '/webhook-debug', element: <WebhookDebug />, page: 'webhook-debug' },
];

// Navigation, grouped by what you're trying to do rather than one flat list of
// thirteen. A group disappears entirely when the user can't access any page in
// it, so an agent on the Basic plan doesn't see empty headings.
const NAV_GROUPS = [
  {
    section: null, // top-level, no heading
    items: [
      { to: '/dashboard', icon: 'dashboard', label: 'Dashboard', page: 'dashboard' },
      { to: '/inbox', icon: 'inbox', label: 'Inbox', page: 'inbox' },
    ],
  },
  {
    section: 'Reach customers',
    items: [
      { to: '/contacts', icon: 'contacts', label: 'Contacts', page: 'contacts' },
      { to: '/broadcasts', icon: 'broadcasts', label: 'Broadcasts', page: 'broadcasts' },
      { to: '/templates', icon: 'templates', label: 'Templates', page: 'templates' },
    ],
  },
  {
    section: 'Sell',
    items: [
      { to: '/catalog', icon: 'catalog', label: 'Catalog', page: 'catalog' },
      { to: '/quotations', icon: 'quotations', label: 'Quotations', page: 'quotations' },
    ],
  },
  {
    section: 'Automate',
    items: [
      { to: '/flow-triggers', icon: 'flow', label: 'Flow Triggers', page: 'flow-triggers' },
      { to: '/automations', icon: 'automations', label: 'Automations', page: 'automations' },
    ],
  },
  {
    section: 'Measure',
    items: [
      { to: '/analytics', icon: 'analytics', label: 'Analytics', page: 'analytics' },
      { to: '/links', icon: 'links', label: 'Link Tracker', page: 'links' },
      { to: '/wfm', icon: 'workforce', label: 'Workforce', page: 'wfm' },
    ],
  },
  {
    section: 'Manage',
    items: [
      { to: '/settings', icon: 'settings', label: 'Settings', page: 'settings' },
      { to: '/webhook-debug', icon: 'webhook', label: 'Webhook Debug', page: 'webhook-debug' },
      { to: '/faq', icon: 'help', label: 'Help & FAQ', page: 'faq' },
    ],
  },
];

// A page is accessible if the user is an admin, it's an always-on page, or the
// server-resolved access list (access_pages) includes it.
const ALWAYS_ON = ['dashboard', 'faq'];
const isSuperAdmin = user => user?.role === 'super_admin';
const isAdmin = user => user?.role === 'admin' || isSuperAdmin(user);
function canAccess(user, page) {
  if (!user) return false;
  if (isAdmin(user) && (!user.active_workspace || String(user.active_workspace) === String(user._id))) return true;
  if (ALWAYS_ON.includes(page)) return true;
  return (user.access_pages || []).includes(page);
}

function Sidebar({ theme, toggleTheme, mobileOpen, onNavigate }) {
  const { user, logout } = useAuth();
  const wa = user?.wa_config;
  // Chats assigned to me that I haven't seen yet — chimes and badges the
  // Inbox nav item from anywhere in the app, clears when the inbox is opened.
  const inboxBadge = useAssignmentAlert(user);

  return (
    <nav className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <img className="logo-icon" src="/app-icon.png" alt="" />
        <span className="logo-text">TuWay Africa</span>
        <NotificationBell />
      </div>

      {/* WABA connection alert — agents share their admin's WhatsApp connection,
          so they're pointed at the admin instead of a Settings page they can't open. */}
      {!wa?.connected && (
        <div className="sidebar-alert">
          <span>⚠️</span>
          <span>{user?.team_role === 'agent' ? 'WhatsApp not connected — contact your admin' : 'Connect WhatsApp in Settings'}</span>
        </div>
      )}

      <ul className="nav-list">
        {NAV_GROUPS.map(group => {
          const items = group.items.filter(n => canAccess(user, n.page));
          if (!items.length) return null;
          return (
            <li key={group.section || 'top'} className="nav-group">
              {group.section && <div className="nav-section">{group.section}</div>}
              <ul style={{ listStyle: 'none' }}>
                {items.map(n => (
                  <li key={n.to}>
                    <NavLink to={n.to} onClick={onNavigate} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                      <NavIcon name={n.icon} />
                      <span className="nav-label">{n.label}</span>
                      {n.page === 'inbox' && inboxBadge > 0 && (
                        <span className="nav-badge" aria-label={`${inboxBadge} unread`}>
                          {inboxBadge > 9 ? '9+' : inboxBadge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
        {isAdmin(user) && (
          <li className="nav-group">
            <div className="nav-section">Administration</div>
            <ul style={{ listStyle: 'none' }}>
              <li>
                <NavLink to="/admin" end onClick={onNavigate} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                  <NavIcon name="admin" />
                  <span className="nav-label">Admin</span>
                </NavLink>
              </li>
              {isSuperAdmin(user) && (
                <>
                  <li>
                    <NavLink to="/admin/usage" onClick={onNavigate} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                    <NavIcon name="analytics" />
                      <span className="nav-label">Usage</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/admin/monitoring" onClick={onNavigate} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                    <NavIcon name="monitoring" />
                      <span className="nav-label">Monitoring</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/admin/audit" onClick={onNavigate} className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                    <NavIcon name="audit" />
                      <span className="nav-label">Audit Trail</span>
                    </NavLink>
                  </li>
                </>
              )}
            </ul>
          </li>
        )}
      </ul>

      {/* Presence: Available / Break / Not Ready / Email / Offline, with the
          live timer and today's remaining break allowance. */}
      <AgentStatusMenu />

      <div className="sidebar-user">
        <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
        <div className="user-info">
          <div className="user-name">{user?.name}</div>
          {wa?.connected
            ? <div className="user-wa-status connected">● WA Connected</div>
            : <div className="user-wa-status disconnected">● WA Disconnected</div>}
        </div>
        <button className="btn-theme" onClick={toggleTheme} title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <button className="btn-logout" onClick={logout} title="Sign out">⎋</button>
      </div>
    </nav>
  );
}

const NAV_ICON_PATHS = {
  dashboard: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
  inbox: <><path d="M4 5h16v11H8l-4 4V5Z" /><path d="M8 9h8M8 12h5" /></>,
  contacts: <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V20" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14h1a4 4 0 0 1 4 4v2" /></>,
  broadcasts: <><path d="m4 13 4-2 9-5v12l-9-5-4-2v2Z" /><path d="m8 13 1.5 6h3L11 14" /></>,
  templates: <><path d="M5 3h10l4 4v14H5V3Z" /><path d="M15 3v5h4M8 12h8M8 16h6" /></>,
  catalog: <><path d="m4 7 8-4 8 4-8 4-8-4Z" /><path d="M4 7v10l8 4 8-4V7M12 11v10" /></>,
  quotations: <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
  flow: <><path d="M13 2 5 14h7l-1 8 8-12h-7l1-8Z" /></>,
  automations: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1v.1h-4v-.1a1.7 1.7 0 0 0-1.1-1.6 1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1-.4h-.1v-4H3a1.7 1.7 0 0 0 1.6-1.1 1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1v-.1h4V3a1.7 1.7 0 0 0 1.1 1.6 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.14.37.36.7.6 1 .25.28.6.4 1 .4h.1v4H21a1.7 1.7 0 0 0-1.6.6Z" /></>,
  analytics: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
  links: <><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.15 1.15" /><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.15-1.15" /></>,
  workforce: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a7.7 7.7 0 0 0 .1-6l2-1.5-2-3.4-2.4 1A8 8 0 0 0 12 3L11.7.5h-4L7.4 3A8 8 0 0 0 4 5.1l-2.4-1-2 3.4 2 1.5a7.7 7.7 0 0 0 .1 6l-2 1.5 2 3.4 2.4-1A8 8 0 0 0 9 21l.3 2.5h4l.3-2.5a8 8 0 0 0 3.4-2.1l2.4 1 2-3.4-2-1.5Z" transform="scale(.82) translate(2.6 2.6)" /></>,
  webhook: <><path d="M8 12h8M12 8v8" /><rect x="3" y="3" width="18" height="18" rx="4" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.6 2.6 0 1 1 4.2 2c-1 .8-1.7 1.2-1.7 2.5M12 17h.01" /></>,
  admin: <><path d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6l8-3Z" /><path d="m9 12 2 2 4-4" /></>,
  monitoring: <><path d="M3 12h4l2-5 4 10 2-5h6" /></>,
  audit: <><path d="M6 3h12v18H6zM9 8h6M9 12h6M9 16h4" /></>,
};

function NavIcon({ name }) {
  return <span className="nav-icon" aria-hidden="true"><svg viewBox="0 0 24 24">{NAV_ICON_PATHS[name] || NAV_ICON_PATHS.help}</svg></span>;
}
